// Rain on Cotton Paper — preset Component.
// Self-contained: own DOM refs, own intervals/timeouts, own SVG state.
// Lifecycle: mounted → start() → onComplete() OR destroy() (via unmount).
// Live-updatable via prop changes for: ripple speed (CSS var), distortion
// strength (filter toggle), background color/image, typography (CSS vars).
// Replay-required props handled by parent passing a `replayKey` that
// changes → React unmount/remount.

import React, { useEffect, useMemo, useRef } from 'react';
import './rainOnCotton.css';
import { defaults } from './defaults';

// Phase 1.9.14 — text choreography constants. Per-letter cadences are
// now driven by settings (revealSpeed / flySpeed). textRevealDelay is
// derived from waterRipplesEnabled so the longer 4.6s intro is only
// used when ripples are carrying the prelude.
const TEXT_REVEAL_DELAY_QUIET   = 1200; // ms — no ripples
const TEXT_REVEAL_DELAY_RIPPLES = 4600; // ms — ripples on
const BASE_DROPS_PER_SECOND = 1.3;
const NEAR_TEXT_DROP_CHANCE = 0.24;

function rnd(min, max) { return min + Math.random() * (max - min); }

// Fisher–Yates shuffle. Replaces `.sort(() => Math.random() - 0.5)` so
// the reveal order is uniformly random and stable across browsers.
function shuffleIndices(n) {
  const arr = new Array(n);
  for (let i = 0; i < n; i++) arr[i] = i;
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = arr[i]; arr[i] = arr[j]; arr[j] = t;
  }
  return arr;
}

// Phase 1.9.14 — restrained, per-character variation per direction.
// Ranges chosen so letters rise individually rather than scatter.
const FLY_DIRECTION_RANGES = {
  'up':         { x: [-24, 24],   y: [-110, -45], rotation: [-12, 12] },
  'up-right':   { x: [35, 105],   y: [-110, -40], rotation: [-12, 16] },
  'up-left':    { x: [-105, -35], y: [-110, -40], rotation: [-16, 12] },
  'down-right': { x: [35, 105],   y: [40, 110],   rotation: [-12, 16] },
  'down-left':  { x: [-105, -35], y: [40, 110],   rotation: [-16, 12] },
  'soft-out':   { x: [-70, 70],   y: [-65, 65],   rotation: [-10, 10] },
};

function getFlyValues(direction) {
  const r = FLY_DIRECTION_RANGES[direction] || FLY_DIRECTION_RANGES['up'];
  return {
    x: rnd(r.x[0], r.x[1]),
    y: rnd(r.y[0], r.y[1]),
    rotation: rnd(r.rotation[0], r.rotation[1]),
  };
}

export default function RainOnCotton({ settings, onComplete, reducedMotion = false, autoStart = true, startRef }) {
  const s = useMemo(() => ({ ...defaults, ...settings }), [settings]);

  const rootRef    = useRef(null);
  const poemRef    = useRef(null);
  const rippleRef  = useRef(null);
  const lensRef    = useRef(null);
  const finalRef   = useRef(null);
  const filterRef  = useRef(null);
  const completedRef = useRef(false);

  const intervalsRef = useRef([]);
  const timeoutsRef  = useRef([]);
  const charsRef     = useRef([]);

  // ── Apply live-updatable settings whenever they change ───────────
  useEffect(() => {
    const root = rootRef.current; if (!root) return;
    root.style.setProperty('--ripple-duration-scale', String(s.rippleDurationScale ?? 1));
    root.style.setProperty('--roc-bg', s.backgroundColor || '#ebe7df');
    root.style.setProperty('--roc-open-font',  `'${s.openingTypography?.fontFamily || 'Cormorant Garamond'}'`);
    root.style.setProperty('--roc-open-color', s.openingTypography?.color || '#3b342c');
    root.style.setProperty('--roc-open-scale', String(s.openingTypography?.sizeScale ?? 1));
    root.style.setProperty('--roc-final-font',  `'${s.finalTypography?.fontFamily || 'Cormorant Garamond'}'`);
    root.style.setProperty('--roc-final-color', s.finalTypography?.color || '#3b342c');
    root.style.setProperty('--roc-final-scale', String(s.finalTypography?.sizeScale ?? 1));
    // Phase 1.9.9/1.9.10 — Background opacity. Drives `.roc-paper-bg`'s
    // opacity only. Clamped to the schema range (0.50 – 1.00). The
    // invitation hero is rendered at full opacity underneath; this
    // slider controls how much of it shows through the paper layer.
    // Legacy openers without this field fall back to 0.94.
    const rawBgOp = typeof s.backgroundOpacity === 'number' ? s.backgroundOpacity : 0.94;
    const bgOp = Math.min(1, Math.max(0.5, rawBgOp));
    root.style.setProperty('--roc-bg-opacity', String(bgOp));
    // Phase 1.9.12 — Backdrop blur. Clamped 0–8 px. Disabled when
    // backgroundOpacity is fully opaque (nothing underneath is visible
    // so blur would just cost a paint for no perceptible gain).
    const rawBlur = typeof s.backdropBlur === 'number' ? s.backdropBlur : 0;
    const blurPx = Math.min(8, Math.max(0, Math.round(rawBlur)));
    const effectiveBlur = (bgOp >= 0.999 || blurPx === 0) ? 0 : blurPx;
    root.style.setProperty('--roc-backdrop-blur', `${effectiveBlur}px`);
    // Phase 1.9.15 — per-stage timing CSS vars. Read by the character
    // is-flying transition and the final-text opacity transition.
    const flyDur   = Math.max(1, s.flyDuration       ?? 2400);
    const fadeDur  = Math.max(1, s.finalFadeDuration ?? 2200);
    root.style.setProperty('--roc-fly-duration', `${flyDur}ms`);
    root.style.setProperty('--roc-final-fade',   `${fadeDur}ms`);
  }, [s]);

  // Toggle distortion filter class (only when strength > 0) — perf-aware.
  useEffect(() => {
    const poem = poemRef.current; if (!poem) return;
    if ((s.distortionStrength || 0) > 0) poem.classList.add('has-distortion');
    else poem.classList.remove('has-distortion');
  }, [s.distortionStrength]);

  // Background image overlay.
  useEffect(() => {
    const root = rootRef.current; if (!root) return;
    const bg = root.querySelector('.roc-paper-bg'); if (!bg) return;
    if (s.backgroundMode === 'image' && s.backgroundImageUrl) {
      bg.classList.add('with-image');
      root.style.setProperty('--roc-bg-image', `url("${s.backgroundImageUrl}")`);
    } else {
      bg.classList.remove('with-image');
      root.style.removeProperty('--roc-bg-image');
    }
  }, [s.backgroundMode, s.backgroundImageUrl]);

  // ── Main lifecycle — runs once per mount. Parent triggers Replay by
  // changing key, which unmounts/remounts this component cleanly. ─────
  useEffect(() => {
    const poem = poemRef.current;
    const ripple = rippleRef.current;
    const lens = lensRef.current;
    const finalScreen = finalRef.current;
    if (!poem || !ripple || !lens || !finalScreen) return;

    const intervals = intervalsRef.current;
    const timeouts  = timeoutsRef.current;
    const chars     = [];
    charsRef.current = chars;

    const safeOnComplete = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      onComplete?.();
    };

    // ── Build poem letter-by-letter ─────────────────────────────────
    poem.textContent = '';
    const lines = (s.openingText || '').split('\n');
    lines.forEach((lineText) => {
      const lineSpan = document.createElement('span');
      lineSpan.className = 'roc-line';
      lineText.split('').forEach((ch) => {
        const c = document.createElement('span');
        c.className = 'roc-char';
        c.textContent = ch === ' ' ? '\u00A0' : ch;
        const fly = getFlyValues(s.flyDirection || 'up');
        c.style.setProperty('--fly-x', `${fly.x}px`);
        c.style.setProperty('--fly-y', `${fly.y}px`);
        c.style.setProperty('--fly-rotation', `${fly.rotation}deg`);
        lineSpan.appendChild(c);
        chars.push(c);
      });
      poem.appendChild(lineSpan);
    });

    finalScreen.textContent = s.finalText || '';
    finalScreen.classList.remove('visible');

    // ── Reduced motion path ─────────────────────────────────────────
    // Phase 1.9.18 — also gate-aware. When autoStart is false the host
    // is waiting for the guest's tap to begin; we expose a kickReduced()
    // entry point and only schedule the timers once it fires.
    if (reducedMotion) {
      const kickReduced = () => {
        const t1 = setTimeout(() => {
          chars.forEach((c) => c.classList.add('is-revealed'));
        }, 200);
        const t2 = setTimeout(() => {
          finalScreen.classList.add('visible');
        }, 1800);
        const t3 = setTimeout(safeOnComplete, 4500);
        timeouts.push(t1, t2, t3);
      };
      if (autoStart) kickReduced();
      else if (startRef) startRef.current = kickReduced;
      return cleanup;
    }

    // ── Rain ────────────────────────────────────────────────────────
    // Phase 1.9.13 — Water ripples are now opt-in. When disabled we
    // skip the intro drops, the recurring spawn interval, the lens DOM
    // creation, and the SVG distortion trigger entirely. The hidden
    // ripple/lens DOM containers remain mounted (empty) so cleanup is
    // a no-op and the layout stays identical; they're cheap.
    // Phase 1.9.18 — kicked from startTimeline() so the rain doesn't
    // start until the guest taps (when the opener is gesture-gated).
    let rainSpawner = null;
    const ripplesOn = s.waterRipplesEnabled === true;
    if (ripplesOn) {
      const dropsPerSec = BASE_DROPS_PER_SECOND * (s.rainDensity ?? 1);
      const rainIntervalMs = 1000 / Math.max(0.3, dropsPerSec);
      const introDrops = Math.max(2, Math.round(7 * (s.rainDensity ?? 1)));

      const isDropOnText = (x, y) => {
        const r = poem.getBoundingClientRect();
        const rootR = rootRef.current.getBoundingClientRect();
        const lx = x + rootR.left, ly = y + rootR.top;
        return lx > r.left - 24 && lx < r.right + 24 && ly > r.top - 18 && ly < r.bottom + 18;
      };

      const triggerDistortion = (strength) => {
        const f = filterRef.current; if (!f) return;
        let frame = 0; const total = 38;
        const itv = setInterval(() => {
          frame += 1;
          const p = frame / total;
          const e = Math.pow(1 - p, 2.2);
          f.setAttribute('scale', (strength * e).toFixed(2));
          if (frame >= total) { clearInterval(itv); f.setAttribute('scale', '0'); }
        }, 45);
        intervals.push(itv);
      };

      const createDrop = (x, y, size) => {
        const group = document.createElement('div');
        group.className = 'roc-ripple-group';
        group.style.left = `${x}px`; group.style.top = `${y}px`;
        const rippleSize = size ?? rnd(34, 52);
        group.style.setProperty('--ripple-size', `${rippleSize}px`);
        for (let i = 1; i <= 2; i++) {
          const w = document.createElement('div');
          w.className = `roc-wave roc-wave-${i}`;
          group.appendChild(w);
        }
        ripple.appendChild(group);
        if (isDropOnText(x, y) && (s.distortionStrength ?? 0) > 0) {
          triggerDistortion(s.distortionStrength);
          const lensEl = document.createElement('div');
          lensEl.className = 'roc-lens';
          lensEl.style.left = `${x}px`; lensEl.style.top = `${y}px`;
          lensEl.style.setProperty('--lens-size', `${rnd(42, 58)}px`);
          lens.appendChild(lensEl);
          lensEl.addEventListener('animationend', () => lensEl.remove(), { once: true });
        }
        const w2 = group.querySelector('.roc-wave-2');
        w2.addEventListener('animationend', () => group.remove(), { once: true });
      };

      const spawnDrop = () => {
        const rootR = rootRef.current.getBoundingClientRect();
        const W = rootR.width, H = rootR.height;
        const r = poem.getBoundingClientRect();
        const localR = { left: r.left - rootR.left, top: r.top - rootR.top, right: r.right - rootR.left, bottom: r.bottom - rootR.top };
        const onText = Math.random() < NEAR_TEXT_DROP_CHANCE;
        const x = onText ? rnd(localR.left + 10, localR.right - 10)  : rnd(0.06 * W, 0.94 * W);
        const y = onText ? rnd(localR.top + 8,  localR.bottom - 8)   : rnd(0.06 * H, 0.94 * H);
        createDrop(x, y);
      };

      // Phase 1.9.18 — actual spawning is deferred until startTimeline()
      // (called either immediately when autoStart=true or on the guest's
      // tap when the opener is gesture-gated for music).
      rainSpawner = () => {
        for (let i = 0; i < introDrops; i++) {
          timeouts.push(setTimeout(spawnDrop, i * rainIntervalMs));
        }
        intervals.push(setInterval(spawnDrop, rainIntervalMs));
      };
    }

    // ── Reveal → Hold → Dissolve → Final → onComplete ───────────────
    // Phase 1.9.14 — settings-driven cadences with Fisher-Yates shuffles
    // so each replay produces a fresh, uniformly-random order.
    // Phase 1.9.15 — fly transition duration + final fade + final hold
    // are now individually controllable. Completion is gated on the
    // actual fly transition end, then fade-in, then designer hold.
    const revealSpeed = Math.max(1, s.revealSpeed ?? 40);
    const flySpeed    = Math.max(1, s.flySpeed    ?? 20);
    const holdAfter   = s.holdTime ?? 4200;
    const flyDurMs    = Math.max(1, s.flyDuration       ?? 2400);
    const finalFade   = Math.max(1, s.finalFadeDuration ?? 2200);
    const finalHold   = Math.max(1, s.finalHoldTime     ?? 2200);

    const revealText = () => {
      const order = shuffleIndices(chars.length);
      order.forEach((randomIndex, orderIndex) => {
        timeouts.push(setTimeout(() => {
          chars[randomIndex]?.classList.add('is-revealed');
        }, orderIndex * revealSpeed));
      });
      const revealDuration = chars.length * revealSpeed;
      timeouts.push(setTimeout(dissolve, revealDuration + holdAfter));
    };

    const dissolve = () => {
      const order = shuffleIndices(chars.length);
      order.forEach((randomIndex, orderIndex) => {
        timeouts.push(setTimeout(() => {
          chars[randomIndex]?.classList.remove('is-revealed');
          chars[randomIndex]?.classList.add('is-flying');
        }, orderIndex * flySpeed));
      });
      // Last character starts at (n-1)*flySpeed and its transition lasts
      // flyDurMs. Show the final text once the last letter has cleared.
      const flyEndMs = chars.length * flySpeed + flyDurMs;
      timeouts.push(setTimeout(() => finalScreen.classList.add('visible'), flyEndMs));
      // Wait for the fade-in to finish, then hold the final text for the
      // designer-controlled finalHoldTime before completing the opener.
      timeouts.push(setTimeout(safeOnComplete, flyEndMs + finalFade + finalHold));
    };

    // Phase 1.9.14 — text intro delay derives from whether ripples are
    // carrying the prelude. Settings.textRevealDelay overrides if set.
    const textRevealDelay = typeof s.textRevealDelay === 'number'
      ? s.textRevealDelay
      : (s.waterRipplesEnabled ? TEXT_REVEAL_DELAY_RIPPLES : TEXT_REVEAL_DELAY_QUIET);

    // Phase 1.9.18 — autoStart contract. When false, the host (e.g. a
    // tap-gated opener for a music-enabled invitation) wants us to mount
    // the first frame and wait. We expose start() via startRef; the host
    // calls it on the guest tap so the animation and music begin
    // together. When autoStart is true (default), behaviour is identical
    // to previous phases.
    //
    // Note: the rain spawn loop is intentionally NOT kicked off here.
    // That's intentional — the paper background is the entire visible
    // pre-tap composition; rain spawning + ripples + distortion all
    // belong to the timeline that begins on tap.
    const startTimeline = () => {
      // Rain (only if enabled) is also part of the timeline.
      if (rainSpawner) rainSpawner();
      timeouts.push(setTimeout(revealText, textRevealDelay));
    };
    if (autoStart) startTimeline();
    else if (startRef) startRef.current = startTimeline;

    function cleanup() {
      intervals.forEach(clearInterval); intervals.length = 0;
      timeouts.forEach(clearTimeout);   timeouts.length = 0;
      // Detach DOM nodes added imperatively — React will unmount the
      // wrappers, but pending animations may still hold references.
      if (ripple) ripple.replaceChildren();
      if (lens)   lens.replaceChildren();
      if (poem)   poem.replaceChildren();
      if (finalScreen) finalScreen.classList.remove('visible');
      if (filterRef.current) filterRef.current.setAttribute('scale', '0');
      chars.length = 0;
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Single lifecycle per mount. Replay = parent changes key.

  return (
    <div ref={rootRef} className={`roc-root ${reducedMotion ? 'reduced' : ''}`}>
      <svg className="roc-distortion-svg" aria-hidden="true">
        <defs>
          <filter id="roc-water-refraction">
            <feTurbulence type="fractalNoise" baseFrequency="0.018" numOctaves="2" seed="8" result="noise" />
            <feDisplacementMap ref={filterRef} in="SourceGraphic" in2="noise" scale="0" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div className="roc-paper-bg" />
      <div className="roc-ripple-layer" ref={rippleRef} />
      <div className="roc-lens-layer"   ref={lensRef} />
      <p   className="roc-poem"          ref={poemRef} />
      <div className="roc-final"         ref={finalRef} />
    </div>
  );
}
